import { current } from "@reduxjs/toolkit";

// ─── undo/redo history (snapshot stacks held in the workflow slice) ────────
// These helpers operate on the workflow slice's Immer draft: they read/write
// state.vm, state.history.{past,future}, state._coalesceKey, state._dragging.
// Extracted from store.js (B6/S2) so history behavior is testable in
// isolation, mirroring FlexibleLayout's layoutHistory.js.
export const HISTORY_CAP = 50;

// Snapshot the vm BY REFERENCE — no deep clone.
//
// This used to JSON.parse(JSON.stringify(...)) the whole diagram, so a full undo
// stack held HISTORY_CAP (50) independent deep copies of every node and edge.
// That is both the allocation cost on each edit and the reason the state tree
// was enormous.
//
// Holding references is safe because immer never mutates in place: every write
// copies only the path it touches and leaves the previous objects alone, and
// produced state is frozen. So `state.vm.nodeOrder.push(id)` in a later reducer
// cannot disturb an entry that references the older array. Consecutive entries
// now structurally share everything the edit did not touch — 50 entries cost
// ~50 keyed-map objects instead of 50 whole diagrams. Same contract
// FlexibleLayout's layoutHistory.js already relies on: "entries snapshot
// {layout, selection} as bare refs (the normalized layout is immutable)".
//
// `current()` detaches a plain snapshot from the draft. Every caller records
// BEFORE mutating (it is the first statement of each reducer), so the draft is
// untouched here and this returns the previous produced state with no copying.
//
// Deliberately NOT moved out of the store into module-level stacks: a reducer
// writing to module state is impure and would drift from the store under
// DevTools time travel. Dropping the clone removes the actual cost; the dev
// middleware already skips this path (see store.js).
export function snapshotVM(state) {
  const vm = current(state.vm);
  return {
    nodesById: vm.nodesById,
    nodeOrder: vm.nodeOrder,
    edgesById: vm.edgesById,
    edgeOrder: vm.edgeOrder,
    activities: vm.activities,
  };
}

// Push the PRE-mutation vm onto the undo stack, clear redo, enforce the cap.
export function recordHistory(state) {
  state.history.past.push(snapshotVM(state));
  if (state.history.past.length > HISTORY_CAP) state.history.past.shift();
  if (state.history.future.length) state.history.future = [];
}

// Discrete edit: always one entry; resets coalesce/drag tracking.
export function recordDiscrete(state) {
  recordHistory(state);
  state._coalesceKey = null;
  state._dragging = false;
}

// Config edit: one entry per coalesce-key burst.
export function recordConfig(state, key) {
  if (state._coalesceKey !== key) {
    recordHistory(state);
    state._coalesceKey = key;
  }
  state._dragging = false;
}

export function resetHistory(state) {
  state.history.past = [];
  state.history.future = [];
  state._coalesceKey = null;
  state._dragging = false;
}
